/**
 * API client — wrapper de fetch con base URL configurable y auth token.
 *
 * Default: producción (panini.nilabs.co/api). Override con VITE_API_BASE_URL
 * en web/.env.local para apuntar a backend local (ej. http://localhost:8002).
 *
 * Auth:
 * - access_token: localStorage (60min). Se manda como Bearer.
 * - refresh_token: cookie httpOnly `panini_refresh` (30 días) puesta por el
 *   backend. No es accesible desde JS — se manda automáticamente por el
 *   browser gracias a `credentials: "include"`.
 *
 * Auto-refresh on 401:
 * - Si un request devuelve 401, intentamos `POST /auth/refresh` (manda la
 *   cookie + header anti-CSRF), recibimos un access nuevo, reintentamos el
 *   request original UNA vez. El user efectivamente queda logueado por la
 *   vida del refresh (30 días) sin re-login.
 * - Concurrency: si varios requests fallan en paralelo, todos esperan al
 *   mismo refresh (singleton promise) — no spamea el backend.
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://panini.nilabs.co/api";

const TOKEN_KEY = "panini.access_token";

// Endpoints que NO disparan auto-refresh — evita loops y respeta el flow OAuth.
const NO_AUTO_REFRESH_PATHS = new Set([
  "/auth/refresh",
  "/auth/google/url",
  "/auth/google/exchange",
  "/auth/logout",
]);

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  // También limpiamos el legacy key del refresh por si quedó del login anterior.
  localStorage.removeItem("panini.refresh_token");
}

export function isApiConfigured(): boolean {
  return Boolean(BASE_URL);
}

export function apiBaseUrl(): string {
  return BASE_URL;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public detail?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

// Singleton promise — si varios requests fallan en paralelo, todos esperan
// al mismo refresh en lugar de generar N requests duplicados al backend.
let refreshPromise: Promise<boolean> | null = null;

/**
 * Intenta renovar el access_token usando la cookie httpOnly de refresh.
 * Retorna true si el refresh fue exitoso (nuevo access guardado), false si no
 * (cookie expirada/inválida — el caller debería redirigir a login).
 */
export async function tryRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-Panini-Refresh": "1",
        },
        credentials: "include",
      });
      if (!res.ok) {
        clearTokens();
        return false;
      }
      const data = (await res.json()) as { access_token?: string };
      if (data.access_token) {
        setToken(data.access_token);
        return true;
      }
      clearTokens();
      return false;
    } catch {
      // Network error → no clearTokens (el user puede estar offline temporalmente)
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function api<T>(path: string, init: RequestInit = {}, _isRetry = false): Promise<T> {
  if (!BASE_URL) throw new ApiError(0, "API base URL no configurada (VITE_API_BASE_URL)");

  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type") && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers,
    // Habilita el envío automático de la cookie httpOnly del refresh.
    credentials: "include",
  });

  // Auto-refresh on 401: el access expiró pero la cookie de refresh puede
  // seguir viva. Renovamos transparentemente y reintentamos UNA vez.
  if (res.status === 401 && !_isRetry && !NO_AUTO_REFRESH_PATHS.has(path)) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      return api<T>(path, init, true);
    }
  }

  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    const msg = (detail && typeof detail === "object" && "detail" in detail) ? String((detail as { detail: unknown }).detail) : res.statusText;
    throw new ApiError(res.status, msg, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
